<script lang="ts">
export default {
  name: 'WholesalerGroupOverview',
};
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { referenceService, wholesalerGroupService } from '@/services/api';
import { compactNumber, percentage } from '@/utilities/Number';
import { paletteColors, stackedSeriesColors, triSeriesColors } from '@/utilities/Color';
import { Wholesaler, WholesalerGroup } from '@/types/WholesalerGroup';
import GenericFilter from '@/components/GenericFilter.vue';
import { LabelValue } from '@/types/label-value';
import { Sankey } from '@/types/Sankey';
import FilterBar from '@/components/FilterBar.vue';
import { MultiSeries } from '@/types/MultiSeries';
import { useRoute } from 'vue-router';
import { compactMoney } from '@/utilities/Money';
import Statistics from '@/components/Statistics.vue';
import ExportMenu from '@/components/ExportMenu.vue';
import { getChartImages, getHeaderImage, getPNG, exportToPNG, exportToPDF } from '@/utilities/ExportCharts';
import FusionCharts from 'fusioncharts';
import Store from '@/store';

const route = useRoute();

// ------------
//     data
// ------------

const loading = ref(true);
const loadingProgress = ref(0);
const showProgressBar = ref(false);
const statistics = ref(
  [] as {
    key: string;
    value: string;
    heading: string;
    loading: boolean;
    classes: string[];
    toolTip?: string;
  }[],
);
const wholesalerGroup = ref({} as WholesalerGroup);
const premiumTotalByMonthData = ref({});
const premiumTotalVSOthersData = ref({} as MultiSeries);
const premiumByCarrierData = ref({} as Sankey);
const premiumTotalByCoverageTypeData = ref({} as MultiSeries);
const premiumTotalByAgencyData = ref({} as MultiSeries);
const wholesalers = ref([] as LabelValue[]);
const selectedWholesalers = ref([] as number[]);
const agencyTypes = ref([] as LabelValue<string>[]);
const selectedAgencyTypes = ref([] as string[]);
const profitCenters = ref([] as LabelValue[]);
const selectedProfitCenters = ref([] as number[]);

// ------------
//    computed
// ------------

const defaultChart = {
  animation: '1',
  theme: 'cacs',
  showLegend: true,
  decimals: '1',
};

const premiumTotalByMonth = computed(() => {
  return {
    chart: {
      ...defaultChart,
      caption: 'Premium Total by Month',
      xAxisName: 'Month',
      yAxisName: 'Premium (Millions)',
      exportFilename: 'Premium Total by Month',
      paletteColors: [...triSeriesColors].reverse(),
    },
    ...premiumTotalByMonthData.value,
  };
});

const premiumTotalVSOthers = computed(() => {
  return {
    chart: {
      ...defaultChart,
      caption: 'Premium Total vs Others (Trailing 12 Months)',
      xAxisName: 'Wholesaler Group',
      yAxisName: 'Premium',
      exportFilename: 'Premium Total vs Others',
      yAxisValueDecimals: '0',
      rotateLabels: true,
      slantLabels: true,
      showValues: false,
      valueFontSize: 10,
      showSum: true,
      paletteColors: stackedSeriesColors,
    },
    categories: premiumTotalVSOthersData.value.categories,
    dataset: premiumTotalVSOthersData.value.dataset,
  };
});

const premiumByCarrier = computed(() => {
  return {
    chart: {
      ...defaultChart,
      caption: 'Premium by Distribution (Trailing 12 Months)',
      yAxisName: 'Premium (Millions)',
      exportFilename: 'Premium by Distribution',
      showLegend: false,
      numberPrefix: '$',
      linkColor: 'blend',
      nodeLabelFontSize: 13,
      valueFontBold: true,
      labelFontBold: true,
      exportFormats: 'PNG=Export PNG File|SVG=Export SVG for PowerPoint',
    },
    ...premiumByCarrierData.value,
  };
});

const premiumTotalByCoverageType = computed(() => {
  return {
    chart: {
      ...defaultChart,
      caption: 'Premium Total by Coverage Type (Trailing 12 Months)',
      xAxisName: 'Coverage Type',
      yAxisName: 'Premium',
      exportFilename: 'Premium Total by Coverage Type',
      legendPosition: 'right-top',
      showValues: false,
      showSum: true,
      paletteColors: stackedSeriesColors,
    },
    ...premiumTotalByCoverageTypeData.value,
  };
});

const premiumTotalByAgency = computed(() => {
  return {
    chart: {
      ...defaultChart,
      caption: 'Premium by CAC Entity (YTD Comparison)',
      yAxisName: 'Premium',
      forceYAxisValueDecimals: false,
      exportFilename: 'CAC + CAH Premium',
      legendPosition: 'right-top',
      rotateLabels: true,
      slantLabels: true,
      showValues: false,
      showSum: true,
      paletteColors,
    },
    ...premiumTotalByAgencyData.value,
  };
});

const isCustomGroup = computed(() => {
  return route.meta?.routeGroup === 'WholesalerGroupCustom';
});

const idParam = computed(() => {
  return route.params.id as string;
});

const downloadMenuOptions = computed(() => {
  return [
    'KPI Panel',
    'Premium Total By Month',
    'Premium Total vs Others (TTM)',
    'Premium Total by Coverage (TTM)',
    'Premium by CAC Entity (YTD)',
    'Premium by Distribution (TTM)',
  ];
});

const filterApplied = computed(() => {
  return selectedWholesalers.value.length > 0
    || selectedAgencyTypes.value.length > 0
    || selectedProfitCenters.value.length > 0;
});

// ------------
//    methods
// ------------

async function refresh(): Promise<void> {
  loading.value = true;
  loadingProgress.value = 0;
  showProgressBar.value = true;

  const wholesalerGroupId = idParam.value;

  wholesalerGroup.value = isCustomGroup.value
    ? await wholesalerGroupService.customGroupById(wholesalerGroupId)
    : await wholesalerGroupService.findById(wholesalerGroupId);

  statistics.value = [
    { key: 'client-count', value: '', heading: 'Client Count', loading: true, classes: ['dense'] },
    { key: 'policy-count', value: '', heading: 'Policy Count', loading: true, classes: ['dense'] },
    { key: 'premium-ytd', value: '', heading: 'Premium YTD', loading: true, classes: ['dense'] },
    { key: 'premium-previous-year', value: '', heading: 'Premium Previous Year', loading: true, classes: ['dense'] },
    { key: 'premium-ttm', value: '', heading: 'Premium TTM', loading: true, classes: ['dense'] },
    { key: 'marketYield', value: '', heading: 'Market Yield', loading: true, classes: ['dense'], toolTip: 'Market Yield is calculated from billed commission and premium. Fees, audit charges, and policies without commission are excluded.' },
    { key: 'rawYield', value: '', heading: 'Raw Yield', loading: true, classes: ['dense'], toolTip: 'Raw Yield is calculated using total revenue (fees, audit charges, and billed commission) and premium.' },
  ];

  const incrementProgress = () => {
    loadingProgress.value++;
    if (loadingProgress.value >= 6) {
      setTimeout(() => {
        showProgressBar.value = false;
      }, 500);
    }
  };

  if (isCustomGroup.value) {
    wholesalerGroupService.customGroupPremiumTotalByMonth(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByMonthData.value = res; incrementProgress(); });
    wholesalerGroupService.customGroupPremiumTotalVSOthers(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalVSOthersData.value = res; incrementProgress(); });
    wholesalerGroupService.customGroupPremiumByCarrier(wholesalerGroup.value.name, wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumByCarrierData.value = res; incrementProgress(); });
    wholesalerGroupService.customGroupPremiumTotalByCoverageType(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByCoverageTypeData.value = res; incrementProgress(); });
    wholesalerGroupService.customGroupPremiumTotalByAgency(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByAgencyData.value = res; incrementProgress(); });
    wholesalerGroupService.customGroupKpi(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(kpi => {
        statistics.value = [
          { key: 'client-count', value: compactNumber(kpi.clientCount), heading: 'Client Count', loading: false, classes: ['dense'] },
          { key: 'policy-count', value: compactNumber(kpi.policyCount), heading: 'Policy Count', loading: false, classes: ['dense'] },
          { key: 'premium-ytd', value: compactMoney(kpi.premiumYTD), heading: 'Premium YTD', loading: false, classes: ['dense'] },
          { key: 'premium-previous-year', value: compactMoney(kpi.premiumPreviousYear), heading: 'Premium Previous Year', loading: false, classes: ['dense'] },
          { key: 'premium-ttm', value: compactMoney(kpi.premiumTTM), heading: 'Premium TTM', loading: false, classes: ['dense'] },
          { key: 'marketYield', value: percentage(kpi.marketYield), heading: 'Market Yield', loading: false, classes: ['dense'], toolTip: 'Market Yield is calculated from billed commission and premium. Fees, audit charges, and policies without commission are excluded.' },
          { key: 'rawYield', value: percentage(kpi.rawYield), heading: 'Raw Yield', loading: false, classes: ['dense'], toolTip: 'Raw Yield is calculated using total revenue (fees, audit charges, and billed commission) and premium.' },
        ];
        incrementProgress();
      });
  } else {
    wholesalerGroupService.premiumTotalByMonth(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByMonthData.value = res; incrementProgress(); });
    wholesalerGroupService.premiumTotalVSOthers(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalVSOthersData.value = res; incrementProgress(); });
    wholesalerGroupService.premiumByCarrier(wholesalerGroup.value.name, wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumByCarrierData.value = res; incrementProgress(); });
    wholesalerGroupService.premiumTotalByCoverageType(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByCoverageTypeData.value = res; incrementProgress(); });
    wholesalerGroupService.premiumTotalByAgency(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value, selectedProfitCenters.value)
      .then(res => { premiumTotalByAgencyData.value = res; incrementProgress(); });
    wholesalerGroupService.kpi(wholesalerGroupId, selectedWholesalers.value, selectedAgencyTypes.value)
      .then(kpi => {
        statistics.value = [
          { key: 'client-count', value: compactNumber(kpi.clientCount), heading: 'Client Count', loading: false, classes: ['dense'] },
          { key: 'policy-count', value: compactNumber(kpi.policyCount), heading: 'Policy Count', loading: false, classes: ['dense'] },
          { key: 'premium-ytd', value: compactMoney(kpi.premiumYTD), heading: 'Premium YTD', loading: false, classes: ['dense'] },
          { key: 'premium-previous-year', value: compactMoney(kpi.premiumPreviousYear), heading: 'Premium Previous Year', loading: false, classes: ['dense'] },
          { key: 'premium-ttm', value: compactMoney(kpi.premiumTTM), heading: 'Premium TTM', loading: false, classes: ['dense'] },
          { key: 'marketYield', value: percentage(kpi.marketYield), heading: 'Market Yield', loading: false, classes: ['dense'], toolTip: 'Market Yield is calculated from billed commission and premium. Fees, audit charges, and policies without commission are excluded.' },
          { key: 'rawYield', value: percentage(kpi.rawYield), heading: 'Raw Yield', loading: false, classes: ['dense'], toolTip: 'Raw Yield is calculated using total revenue (fees, audit charges, and billed commission) and premium.' },
        ];
        incrementProgress();
      });
  }

  const [wholesalersList, agencyTypeList, profitCentersList] = await Promise.all([
    isCustomGroup.value
      ? wholesalerGroupService.wholesalersByCustomGroup(wholesalerGroupId)
      : wholesalerGroupService.wholesalers(wholesalerGroupId),
    referenceService.agencyTypes(),
    referenceService.profitCenters(),
  ]);

  wholesalers.value = wholesalersList.map((w: Wholesaler): LabelValue => ({ label: w.name, value: w.id }));
  agencyTypes.value = agencyTypeList.map((a) => ({ label: a.agencyName, value: a.agencyCode }));
  profitCenters.value = profitCentersList.map((p) => ({ label: p.name, value: p.id }));

  loading.value = false;
}

function filterSelectionsChanged(): Promise<void> {
  return refresh();
}

async function handleExport(type: string, charts: string[], finishExport: () => void): Promise<void> {
  // gather element IDs for each chart
  const elementIds = charts
    .map((c) => {
      if (c === 'KPI Panel') {
        return 'kpiPanel';
      } else if (c === 'Premium Total By Month') {
        return 'premiumTotalByMonthChart';
      } else if (c === 'Premium Total vs Others (TTM)') {
        return 'premiumTotalVSOthersChart';
      } else if (c === 'Premium Total by Coverage (TTM)') {
        return 'premiumByCoverageChart';
      } else if (c === 'Premium by CAC Entity (YTD)') {
        return 'premiumByAgencyChart';
      } else if (c === 'Premium by Distribution (TTM)') {
        return 'premiumByCarrierChart';
      } else {
        console.log(`unexpected chart ${c}`);
        return null;
      }
    })
    .filter((id) => id !== null) as string[];

  const images = await getChartImages(elementIds);
  const maxWidth = Math.max(...images.map(({ width }) => width));
  const header = await getHeaderImage(maxWidth, wholesalerGroup.value.name);

  // create the PNG image that will be used for exporting
  const png = await getPNG(images, header);

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split('T')[0].replace(/-/g, '.');
  const tenantName = Store.state.user?.tenantName;
  const filename = `${tenantName}-${wholesalerGroup.value.name}_${formattedDate}`;
  
  if (type === 'png') {
    await exportToPNG(png, filename + '.png');
  } else if (type === 'pdf') {
    await exportToPDF(images, header, filename + '.pdf');
  } else {
    console.log(`unexpected value ${type} for export type`);
  }
  finishExport();
}

async function resizeCharts(): Promise<void> {
  // resize this particular chart because it doesn't resize automatically
  FusionCharts.items['premiumByCarrierChart'].render();
}

function clearAllFilters(): void {
  selectedWholesalers.value = [];
  selectedAgencyTypes.value = [];
  selectedProfitCenters.value = [];
  refresh();
}

// ------------
//   lifecycle
// ------------

onMounted(async () => {
  window.addEventListener('resize', resizeCharts);
  await refresh();
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeCharts);
});
</script>

<template>
  <div>
    <div class="tw:flex">
      <div>
        <FilterBar>
          <GenericFilter
            title="Wholesaler"
            static-chip-title
            :types="wholesalers"
            v-model="selectedWholesalers"
            @update:model-value="filterSelectionsChanged" />

          <GenericFilter
            title="CAC Entity"
            chip-title-field="label"
            :types="agencyTypes"
            v-model="selectedAgencyTypes"
            @update:model-value="filterSelectionsChanged" />

          <GenericFilter
            title="Profit Center"
            chip-title-field="label"
            :types="profitCenters"
            v-model="selectedProfitCenters"
            @update:model-value="filterSelectionsChanged" />

          <div class="tw:flex tw:items-center">
            <a v-show="filterApplied" @click="clearAllFilters()" class="tw:link">Reset&nbsp;Filters</a>
          </div>
        </FilterBar>
      </div>

      <div class="tw:pb-4 tw:pt-2.5">
        <ExportMenu :items="downloadMenuOptions" @export="handleExport" />
      </div>
    </div>

    <div class="tw:h-1.5 tw:mt-2">
      <transition name="fade">
        <div v-if="showProgressBar" class="tw:flex tw:justify-center">
          <progress class="tw:progress tw:w-56 tw:progress-secondary tw:rounded-none tw:h-2" :value="loadingProgress" max="6"></progress>
        </div>
      </transition>
    </div>

    <div id="wholesaler-header" class="inner">
      <div class="tw:flex tw:flex-col tw:pb-8">
        <Statistics id="kpiPanel" dense no-borders :statistics="statistics" />
        <main v-if="!loading" class="tw:grid tw:grid-cols-[repeat(auto-fit,minmax(680px,1fr))] tw:justify-center tw:gap-4">
          <fusioncharts
            id="premiumTotalByMonthChart"
            type="mscolumn2d"
            width="100%"
            height="400"
            dataFormat="json"
            :dataSource="premiumTotalByMonth" />
          <fusioncharts
            id="premiumTotalVSOthersChart"
            type="stackedcolumn2d"
            width="100%"
            height="400"
            dataFormat="json"
            :dataSource="premiumTotalVSOthers" />
          <fusioncharts
            id="premiumByCoverageChart"
            type="stackedbar2d"
            width="100%"
            height="400"
            dataFormat="json"
            :dataSource="premiumTotalByCoverageType"></fusioncharts>
          <fusioncharts
            id="premiumByAgencyChart"
            type="stackedbar2d"
            width="100%"
            height="400"
            dataFormat="json"
            :dataSource="premiumTotalByAgency"></fusioncharts>
        </main>
        <fusioncharts
          v-if="!loading"
          id="premiumByCarrierChart"
          type="sankey"
          width="100%"
          height="700"
          dataFormat="json"
          :dataSource="premiumByCarrier" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.loader > * {
  display: grid;
  grid-template-columns: repeat(6, auto);
  grid-column-gap: 5rem;
  justify-content: center;
  justify-items: center;
  align-content: center;
  align-items: center;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
